import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, MagnifyingGlass, PencilSimple, Trash, DownloadSimple, Phone as PhoneIcon, Envelope, CalendarBlank, ArrowUp, ArrowDown, MapPin, PhoneCall, Trophy, XCircle, CaretDown, CaretUp, WhatsappLogo, ChatText, Eye, Funnel } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth, isAdmin } from "@/lib/access";

const API = `${window.location.origin}/api`;
const STATUSES = ["New Lead", "Contacted", "Follow-up", "Negotiation", "Closed Won", "Closed Lost"];
const PRIORITIES = ["Low", "Medium", "High"];
const SOURCES = ["Website", "Referral", "Social Media", "Cold Call", "Email Campaign", "Trade Show", "Other"];

const statusColors = {
  "New Lead": "bg-blue-100 text-blue-700 border-blue-200",
  "Contacted": "bg-purple-100 text-purple-700 border-purple-200",
  "Follow-up": "bg-amber-100 text-amber-700 border-amber-200",
  "Negotiation": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Closed Won": "bg-green-100 text-green-700 border-green-200",
  "Closed Lost": "bg-red-100 text-red-700 border-red-200",
};
const priorityColors = { "High": "bg-red-100 text-red-700", "Medium": "bg-amber-100 text-amber-700", "Low": "bg-green-100 text-green-700" };

function getFollowUpStyle(dateStr) {
  if (!dateStr) return { label: "-", style: "text-gray-400", type: "" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.split("-").map(Number);
  const diff = Math.round((new Date(y, m - 1, d) - today) / 86400000);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const label = `${d} ${months[m - 1]}`;
  if (diff < 0) return { label, style: "text-red-600", type: `${Math.abs(diff)}d overdue` };
  if (diff === 0) return { label, style: "text-orange-600", type: "Today" };
  return { label, style: "text-green-600", type: diff === 1 ? "Tomorrow" : `In ${diff}d` };
}

function getProgressColor(completed, required) {
  if (completed >= required) return "bg-green-500";
  if (completed > 0) return "bg-amber-500";
  return "bg-red-400";
}

function getProgressTextColor(completed, required) {
  if (completed >= required) return "text-green-700";
  if (completed > 0) return "text-amber-700";
  return "text-red-600";
}

function FollowUpProgress({ required, completed }) {
  if (!required || required <= 0) return null;
  const pct = Math.min((completed / required) * 100, 100);
  const color = getProgressColor(completed, required);
  const textColor = getProgressTextColor(completed, required);
  return (
    <div className="flex items-center gap-1.5" data-testid="followup-progress">
      <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} /></div>
      <span className={`text-[10px] font-bold ${textColor}`}>{completed}/{required}</span>
    </div>
  );
}

/* ====== Customer Detail Modal ====== */
function CustomerDetail({ lead, open, onClose, onRefresh }) {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logNotes, setLogNotes] = useState("");
  const [nextDate, setNextDate] = useState("");

  const fetchHistory = useCallback(async () => {
    if (!lead) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads/${lead.lead_id}/history`);
      setHistory(res.data.history || []);
      setStats(res.data.stats || null);
    } catch (err) { toast.error("Failed to load history"); }
    finally { setLoading(false); }
  }, [lead]);

  useEffect(() => { if (open && lead) fetchHistory(); }, [open, lead, fetchHistory]);

  const handleLogFollowup = async () => {
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/log-followup`, { notes: logNotes || "Follow-up completed", follow_up_date: nextDate });
      toast.success("Follow-up logged!");
      setLogNotes(""); setNextDate("");
      fetchHistory();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error("Failed"); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/leads/${lead.lead_id}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus}!`);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err) { toast.error("Failed"); }
  };

  if (!lead) return null;
  const fu = getFollowUpStyle(lead.follow_up_date);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Customer Detail</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Customer Info */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{lead.client_name}</h3>
              {lead.company_name && <p className="text-sm text-gray-500">{lead.company_name}</p>}
              {lead.product_interested && <p className="text-sm text-indigo-600">{lead.product_interested}</p>}
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-bold border ${statusColors[lead.status] || "bg-gray-100"}`}>{lead.status}</span>
          </div>

          {/* Contact + Quick Actions */}
          <div className="flex flex-wrap gap-2">
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="flex items-center gap-1 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs font-bold text-green-700 touch-target" data-testid="action-call">
                <PhoneIcon size={14} /> Call
              </a>
            )}
            {lead.phone && (
              <a href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-bold text-emerald-700 touch-target" data-testid="action-whatsapp">
                <WhatsappLogo size={14} /> WhatsApp
              </a>
            )}
            {lead.email && (
              <a href={`mailto:${lead.email}`} className="flex items-center gap-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-blue-700 touch-target" data-testid="action-email">
                <Envelope size={14} /> Email
              </a>
            )}
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-2" data-testid="customer-stats">
              <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-gray-500">First Contact</p>
                <p className="text-sm font-bold text-gray-900">{stats.first_contact || "-"}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-gray-500">Last Follow-up</p>
                <p className="text-sm font-bold text-gray-900">{stats.last_followup || "-"}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-gray-500">Days Active</p>
                <p className="text-sm font-bold text-indigo-600">{stats.days_active}</p>
              </div>
            </div>
          )}

          {/* Follow-up Summary */}
          <div className="grid grid-cols-3 gap-2" data-testid="followup-summary">
            <div className="bg-green-50 rounded-lg p-2.5 text-center border border-green-200">
              <p className="text-lg font-bold text-green-600">{stats?.total_done || lead.followups_completed || 0}</p>
              <p className="text-[10px] text-gray-500 font-medium">Done</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-2.5 text-center border border-indigo-200">
              <p className="text-lg font-bold text-indigo-600">{stats?.total_required || lead.followups_required || 0}</p>
              <p className="text-[10px] text-gray-500 font-medium">Required</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-2.5 text-center border border-amber-200">
              <p className="text-lg font-bold text-amber-600">{stats?.pending || 0}</p>
              <p className="text-[10px] text-gray-500 font-medium">Pending</p>
            </div>
          </div>

          {/* Next Follow-up */}
          {lead.follow_up_date && (
            <div className={`flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 border`}>
              <span className="text-xs text-gray-500">Next Follow-up:</span>
              <span className={`text-sm font-bold ${fu.style}`}>{fu.label} {fu.type ? `(${fu.type})` : ""}</span>
            </div>
          )}

          {/* Add Follow-up */}
          <Card className="p-3 border border-indigo-200 bg-indigo-50/50">
            <p className="text-xs font-bold text-indigo-700 mb-2">Log Follow-up</p>
            <div className="flex gap-2">
              <Input value={logNotes} onChange={(e) => setLogNotes(e.target.value)} placeholder="Notes (e.g. Called, Interested)" className="h-9 text-sm flex-1 bg-white" data-testid="log-notes-input" />
              <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="h-9 text-sm w-[130px] bg-white" data-testid="log-next-date" />
              <Button onClick={handleLogFollowup} size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="log-followup-btn">
                <PhoneCall size={14} className="mr-1" /> Log
              </Button>
            </div>
          </Card>

          {/* Follow-up History Table */}
          <div data-testid="followup-history">
            <p className="text-xs font-bold text-gray-700 mb-2">Follow-up History</p>
            {loading ? (
              <div className="h-16 flex items-center justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
            ) : history.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No follow-up history yet. Log your first follow-up above.</p>
            ) : (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {history.map((h, i) => (
                  <div key={`hist-${i}`} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${h.status === "done" ? "bg-green-500" : "bg-amber-500"}`} />
                      <span className="font-mono text-gray-500">{h.date}</span>
                      <span className={`font-bold ${h.status === "done" ? "text-green-700" : "text-amber-700"}`}>{h.status === "done" ? "Done" : "Pending"}</span>
                    </div>
                    <span className="text-gray-500 truncate ml-2 max-w-[200px]">{h.notes}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Actions */}
          {lead.status !== "Closed Won" && lead.status !== "Closed Lost" && (
            <div className="flex gap-2 pt-2 border-t">
              <Button onClick={() => handleStatusChange("Closed Won")} className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="mark-won-btn">
                <Trophy size={16} className="mr-1" /> Mark as Won
              </Button>
              <Button onClick={() => handleStatusChange("Closed Lost")} variant="outline" className="flex-1 h-10 border-red-300 text-red-600 hover:bg-red-50 font-bold" data-testid="mark-lost-btn">
                <XCircle size={16} className="mr-1" /> Mark as Lost
              </Button>
            </div>
          )}

          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 pt-2 border-t">
            <div><span className="font-medium">Location:</span> {lead.location || "-"}</div>
            <div><span className="font-medium">Source:</span> {lead.lead_source || "-"}</div>
            <div><span className="font-medium">Priority:</span> <span className={`px-1.5 py-0.5 rounded-full font-bold ${priorityColors[lead.priority]}`}>{lead.priority}</span></div>
            <div><span className="font-medium">Assigned:</span> {lead.assigned_to || "-"}</div>
            {lead.notes && <div className="col-span-2"><span className="font-medium">Notes:</span> {lead.notes}</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ====== Compact Lead Card ====== */
function LeadCard({ lead, onView, onEdit, onDelete, onLog }) {
  const [expanded, setExpanded] = useState(false);
  const fu = getFollowUpStyle(lead.follow_up_date);

  return (
    <Card className="border border-gray-200 bg-white overflow-hidden" data-testid={`lead-card-${lead.lead_id}`}>
      {/* Main row - always visible */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-600 flex-shrink-0">
              {lead.client_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <button onClick={() => onView(lead)} className="font-bold text-sm text-gray-900 hover:text-indigo-600 transition-colors truncate block text-left w-full" data-testid={`view-customer-${lead.lead_id}`}>
                {lead.client_name}
              </button>
              <div className="flex items-center gap-1.5 flex-wrap">
                {lead.phone && <span className="text-[10px] text-gray-500 flex items-center gap-0.5"><PhoneIcon size={10} />{lead.phone}</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border ${statusColors[lead.status] || "bg-gray-100"}`}>{lead.status}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {lead.followups_required > 0 && <FollowUpProgress required={lead.followups_required} completed={lead.followups_completed || 0} />}
            {lead.follow_up_date && <span className={`text-[10px] font-bold ${fu.style}`}>{fu.type || fu.label}</span>}
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="flex gap-1">
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="p-1.5 rounded-lg bg-green-50 text-green-600 touch-target" title="Call"><PhoneIcon size={14} weight="bold" /></a>
            )}
            {lead.phone && (
              <a href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 touch-target" title="WhatsApp"><WhatsappLogo size={14} weight="bold" /></a>
            )}
            {(lead.followups_completed || 0) < (lead.followups_required || 0) && (
              <button onClick={(e) => { e.stopPropagation(); onLog(lead); }} className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 touch-target" title="Log Follow-up" data-testid={`quick-log-${lead.lead_id}`}><PhoneCall size={14} weight="bold" /></button>
            )}
            {lead.status !== "Closed Won" && lead.status !== "Closed Lost" && (
              <button onClick={(e) => { e.stopPropagation(); axios.patch(`${API}/leads/${lead.lead_id}/status`, { status: "Closed Won" }).then(() => { toast.success("Marked as Won!"); if (onLog) window.location.reload(); }); }} className="p-1.5 rounded-lg bg-green-50 text-green-600 touch-target" title="Mark as Won"><Trophy size={14} weight="bold" /></button>
            )}
          </div>
          <div className="flex gap-1">
            <button onClick={() => onView(lead)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 touch-target" title="View Details"><Eye size={14} /></button>
            <button onClick={() => onEdit(lead)} className="p-1.5 rounded-lg hover:bg-gray-100 text-indigo-600 touch-target" data-testid={`edit-${lead.lead_id}`}><PencilSimple size={14} /></button>
            <button onClick={() => onDelete(lead)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 touch-target" data-testid={`delete-${lead.lead_id}`}><Trash size={14} /></button>
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 touch-target" data-testid={`expand-${lead.lead_id}`}>
              {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-gray-100 bg-gray-50/50 space-y-1 text-xs text-gray-500" data-testid={`expanded-${lead.lead_id}`}>
          {lead.company_name && <div><span className="font-medium text-gray-700">Company:</span> {lead.company_name}</div>}
          {lead.product_interested && <div><span className="font-medium text-gray-700">Product:</span> {lead.product_interested}</div>}
          {lead.location && <div className="flex items-center gap-1"><MapPin size={11} className="text-red-400" />{lead.location}</div>}
          {lead.email && <div className="flex items-center gap-1"><Envelope size={11} />{lead.email}</div>}
          {lead.lead_source && <div><span className="font-medium text-gray-700">Source:</span> {lead.lead_source}</div>}
          <div><span className="font-medium text-gray-700">Priority:</span> <span className={`px-1.5 py-0.5 rounded-full font-bold ${priorityColors[lead.priority]}`}>{lead.priority}</span></div>
          {lead.assigned_to && <div><span className="font-medium text-gray-700">Assigned:</span> {lead.assigned_to}</div>}
          {lead.notes && <div><span className="font-medium text-gray-700">Notes:</span> {lead.notes}</div>}
          <div className="text-gray-300 font-mono">{lead.lead_id} | {lead.lead_date}</div>
        </div>
      )}
    </Card>
  );
}

/* ====== Main Page ====== */
export default function LeadsPage() {
  const user = useAuth();
  const admin = isAdmin(user);
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterMember, setFilterMember] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewLead, setViewLead] = useState(null);
  const [customSource, setCustomSource] = useState("");
  const [form, setForm] = useState({
    client_name: "", company_name: "", phone: "", email: "", product_interested: "",
    lead_source: "", status: "New Lead", assigned_to: "", follow_up_date: "", priority: "Medium", notes: "",
    lead_date: new Date().toISOString().split("T")[0], location: "", followups_required: 0
  });

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus && filterStatus !== "all") params.status = filterStatus;
      if (filterPriority && filterPriority !== "all") params.priority = filterPriority;
      if (filterMember && filterMember !== "all") params.assigned_to = filterMember;
      if (filterLocation) params.location = filterLocation;
      if (filterDateFrom) params.lead_date_from = filterDateFrom;
      if (filterDateTo) params.lead_date_to = filterDateTo;
      if (search) params.search = search;
      const [leadsRes, teamRes] = await Promise.all([
        axios.get(`${API}/leads`, { params }),
        axios.get(`${API}/team`)
      ]);
      setLeads(leadsRes.data);
      setTeamMembers(admin ? teamRes.data : [{ id: "me", name: user.assignee, status: "Active" }]);
    } catch (err) { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, [filterStatus, filterPriority, filterMember, filterLocation, filterDateFrom, filterDateTo, search, admin, user]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const resetForm = () => setForm({
    client_name: "", company_name: "", phone: "", email: "", product_interested: "",
    lead_source: "", status: "New Lead", assigned_to: "", follow_up_date: "", priority: "Medium", notes: "",
    lead_date: new Date().toISOString().split("T")[0], location: "", followups_required: 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name) { toast.error("Client name required"); return; }
    try {
      const payload = { ...form, followups_required: parseInt(form.followups_required) || 0 };
      if (payload.lead_source === "Other" && customSource) payload.lead_source = customSource;
      if (editing) {
        await axios.put(`${API}/leads/${editing.lead_id}`, payload);
        toast.success("Lead updated!");
      } else {
        await axios.post(`${API}/leads`, payload);
        toast.success("Lead created!");
      }
      setShowDialog(false); setEditing(null); resetForm(); fetchLeads();
    } catch (err) { toast.error("Failed to save lead"); }
  };

  const handleEdit = useCallback((lead) => {
    setEditing(lead);
    setForm({
      client_name: lead.client_name, company_name: lead.company_name, phone: lead.phone,
      email: lead.email, product_interested: lead.product_interested, lead_source: lead.lead_source,
      status: lead.status, assigned_to: lead.assigned_to, follow_up_date: lead.follow_up_date,
      priority: lead.priority, notes: lead.notes,
      lead_date: lead.lead_date || "", location: lead.location || "",
      followups_required: lead.followups_required || 0
    });
    setShowDialog(true);
  }, []);

  const handleDelete = useCallback(async (lead) => {
    if (!window.confirm(`Delete lead ${lead.lead_id}?`)) return;
    try { await axios.delete(`${API}/leads/${lead.lead_id}`); toast.success("Lead deleted"); fetchLeads(); } catch (err) { toast.error("Failed to delete"); }
  }, [fetchLeads]);

  const logFollowup = useCallback(async (lead) => {
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/log-followup`);
      toast.success(`Follow-up logged for ${lead.client_name}!`);
      fetchLeads();
    } catch (err) { toast.error("Failed to log follow-up"); }
  }, [fetchLeads]);

  const hasActiveFilters = !!(filterStatus || filterPriority || filterMember || filterLocation || filterDateFrom || filterDateTo || search);
  const clearAllFilters = () => { setFilterStatus(""); setFilterPriority(""); setFilterMember(""); setFilterLocation(""); setFilterDateFrom(""); setFilterDateTo(""); setSearch(""); };

  const renderedLeads = useMemo(() => {
    return leads.map((lead) => (
      <LeadCard key={lead.lead_id} lead={lead} onView={setViewLead} onEdit={handleEdit} onDelete={handleDelete} onLog={logFollowup} />
    ));
  }, [leads, handleEdit, handleDelete, logFollowup]);

  return (
    <div className="space-y-4 max-w-full" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>CRM Leads</h1>
          <p className="text-xs text-gray-500">{leads.length} leads</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/export/leads`, "_blank")} className="text-xs h-9" data-testid="export-leads-btn">
            <DownloadSimple size={14} className="mr-1" /> Export
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-9" onClick={() => { resetForm(); setEditing(null); setShowDialog(true); }} data-testid="add-lead-btn">
            <Plus size={14} className="mr-1" /> Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex gap-2 items-center overflow-x-auto mobile-scroll-x pb-1">
          <div className="relative flex-1 min-w-[150px]">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchLeads()} className="pl-8 h-9 text-sm" data-testid="lead-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-9 text-xs flex-shrink-0" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterMember} onValueChange={setFilterMember}>
            <SelectTrigger className="w-[130px] h-9 text-xs flex-shrink-0" data-testid="filter-member"><SelectValue placeholder="Team Member" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Members</SelectItem>
              {teamMembers.filter((m) => m.status === "Active").map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={showFilters ? "default" : "outline"} size="sm" className={`h-9 text-xs flex-shrink-0 ${showFilters ? "bg-indigo-600 text-white" : ""}`} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
            <Funnel size={14} className="mr-1" /> Filters
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-9 text-xs flex-shrink-0 text-red-500 hover:text-red-700" onClick={clearAllFilters} data-testid="clear-filters-btn">Clear All</Button>
          )}
        </div>
        {showFilters && (
          <Card className="p-3 border border-indigo-200 bg-indigo-50/30" data-testid="filter-panel">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Location</label>
                <Input value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchLeads()} placeholder="City / Area" className="h-8 text-xs" data-testid="filter-location" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Priority</label>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filter-priority"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Date From</label>
                <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="h-8 text-xs" data-testid="filter-date-from" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 mb-1 block">Date To</label>
                <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="h-8 text-xs" data-testid="filter-date-to" />
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Lead Cards List */}
      <div className="space-y-2" data-testid="leads-list">
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={`skel-${i}`} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : leads.length === 0 ? (
          <Card className="p-8 text-center text-gray-400 border border-gray-200 bg-white">No leads found. Add your first lead!</Card>
        ) : renderedLeads}
      </div>

      {/* Customer Detail Modal */}
      <CustomerDetail lead={viewLead} open={viewLead !== null} onClose={() => setViewLead(null)} onRefresh={fetchLeads} />

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditing(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Gupter', serif" }}>{editing ? "Edit Lead" : "Add New Lead"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Client Name *</label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" className="h-9" data-testid="lead-client-name" /></div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Phone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" className="h-9" data-testid="lead-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Company</label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Company" className="h-9" data-testid="lead-company" /></div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Product</label><Input value={form.product_interested} onChange={(e) => setForm({ ...form, product_interested: e.target.value })} placeholder="Product/Service" className="h-9" data-testid="lead-product" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Follow-up Date</label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="h-9" data-testid="lead-followup" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="h-9" data-testid="lead-email" /></div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Location</label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City / Area" className="h-9" data-testid="lead-location" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Lead Source</label>
                <Select value={form.lead_source} onValueChange={(v) => { setForm({ ...form, lead_source: v }); if (v !== "Other") setCustomSource(""); }}><SelectTrigger className="h-9"><SelectValue placeholder="Source" /></SelectTrigger><SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Assigned To</label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}><SelectTrigger className="h-9"><SelectValue placeholder="Member" /></SelectTrigger><SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{teamMembers.filter((m) => m.status === "Active").map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Lead Date</label><Input type="date" value={form.lead_date} onChange={(e) => setForm({ ...form, lead_date: e.target.value })} className="h-9" data-testid="lead-date" /></div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Follow-ups Req</label><Input type="number" min="0" value={form.followups_required} onChange={(e) => setForm({ ...form, followups_required: parseInt(e.target.value) || 0 })} className="h-9" data-testid="lead-followups-required" /></div>
              <div><label className="text-xs font-medium text-gray-700 mb-1 block">Notes</label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes..." className="h-9" data-testid="lead-notes" /></div>
            </div>
            <Button type="submit" className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="lead-submit-btn">
              {editing ? "Update Lead" : "Create Lead"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
